// meetingUtils.js
// Small formatting helpers shared across the Book a Meeting components.

// The student's IANA timezone (e.g. "Asia/Kolkata"). Sent to the schedule-options
// and schedule endpoints; the backend falls back to the student's tz if omitted.
export const STUDENT_TIMEZONE = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch (_) {
    return "UTC";
  }
})();

// The "other party" on a meeting, from the viewer's perspective:
// a teacher sees the student's name; a student sees the teacher's name.
export const counterpartyName = (meeting, role) =>
  role === "Teacher"
    ? meeting?.student_name || meeting?.teacher_name || ""
    : meeting?.teacher_name || meeting?.student_name || "";

const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const WEEKDAYS_LONG = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
];

// Date -> "YYYY-MM-DD" (what the API expects).
export const toApiDate = (date) => {
  const d = new Date(date);
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

// Date -> "24 June, Wednesday" (the field label style in the screenshots).
export const toDisplayDate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}, ${
    WEEKDAYS_LONG[d.getDay()]
  }`;
};

// "YYYY-MM-DD" + "HH:mm" -> "01/04/2025, at 10:00 AM" (upcoming/history style).
export const toDisplayDateTime = (isoDate, time24) => {
  if (!isoDate) return "";
  const [y, m, d] = isoDate.split("-");
  const datePart = `${d}/${m}/${y}`;
  if (!time24) return datePart;
  const [hRaw, min] = time24.split(":");
  let h = parseInt(hRaw, 10);
  const suffix = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${datePart}, at ${h}:${min} ${suffix}`;
};

// "HH:mm" (24h) -> "10:00 am" label for the time dropdown.
export const to12hLabel = (time24) => {
  if (!time24) return "";
  const [hRaw, min] = time24.split(":");
  let h = parseInt(hRaw, 10);
  const suffix = h >= 12 ? "pm" : "am";
  h = h % 12 || 12;
  return `${h}:${min} ${suffix}`;
};

// "HH:mm" (24h) -> "10:30AM" format (for display)
export const convertTo12Hour = (timeStr) => {
  if (!timeStr) return "";
  const parts = timeStr.split(":");
  if (parts.length < 2) {
    // If format is not HH:mm, return as is
    return timeStr;
  }
  const hour = parseInt(parts[0], 10);
  const minute = parts[1];
  let h = hour;
  const ampm = h >= 12 ? "PM" : "AM";
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${minute}${ampm}`;
};